import { Logger } from '@mobalytics/shared';
import { BraintreeTokenizePayload } from '@paypal/react-paypal-js/dist/types/types/braintree/commonsTypes';
import React from 'react';
import { useHistory, useLocation } from 'react-router';
import { PaymentError } from '../../../payment-error.types';
import type { ProductI } from '../../../payment.types';
import { usePaymentFlowCheckout } from '../use-payment-flow-checkout.hook';
import { usePaymentFlowUpdateBillingInfo } from '../use-payment-flow-update-billing-info.hook';
import { TreasuryPaymentsProvider } from '../../../../../__generated/treasury/types';
import { genPaymentV2ErrorPath, genPaymentV2Path } from '../../../../../utils/formatter/path.formatter';
import { PaypalFlowStepT } from './paypal-flow.types';

type Params = {
  product: ProductI;
  isBundleAdded: boolean;
};

export const usePaypalFlow = (params: Params) => {
  const { product, isBundleAdded } = params;
  const [step, changeStep] = React.useState<PaypalFlowStepT>('paypal-auth');
  const [tokenResponse, setTokenResponse] = React.useState<BraintreeTokenizePayload | null>(null);
  const [loading, setIsLoading] = React.useState(false);
  const { search } = useLocation();
  const { replace } = useHistory();
  const { checkout } = usePaymentFlowCheckout(TreasuryPaymentsProvider.BRAINTREE);
  const { updateBillingInfo } = usePaymentFlowUpdateBillingInfo(TreasuryPaymentsProvider.BRAINTREE);

  const onPaypalApprove = (payload: BraintreeTokenizePayload) => {
    setTokenResponse(payload);
    changeStep('billing-info');
  };

  const processPayment = async () => {
    if (!product.id) {
      Logger.error('[usePaypalFlowNew]:[processPayment]: product.id is missing');
      replace(genPaymentV2ErrorPath(product.id, product.price.id, search, PaymentError.INTERNAL_ERROR));
      return;
    }
    if (!tokenResponse?.nonce) {
      Logger.error('[usePaypalFlowNew]:[processPayment]: nonce is missing');
      replace(genPaymentV2ErrorPath(product.id, product.price.id, search, PaymentError.PAYMENT_DECLINED));
      return;
    }

    const { id: priceId } = product.price;
    const planId = product.__typename === 'TreasuryProductBattlePass' ? (product.bundle?.planId ?? null) : null;
    const coupon = product.__typename === 'TreasuryProductBattlePass' ? (product.bundle?.coupon ?? null) : null;

    try {
      setIsLoading(true);
      await updateBillingInfo(tokenResponse.nonce);
      const response = await checkout([{ priceId }], planId && isBundleAdded ? { planId, coupon } : undefined);
      if (response.errors) {
        Logger.error('[usePaypalFlowNew]:[processPayment]: checkout error', response.errors);
        replace(genPaymentV2ErrorPath(product.id, priceId, search, PaymentError.INTERNAL_ERROR));
        return;
      }
      if (response.data?.treasury?.payments?.checkout?.error) {
        Logger.error(
          '[usePaypalFlowNew]:[processPayment]: checkout error',
          response.data.treasury.payments.checkout.error
        );
        replace(genPaymentV2ErrorPath(product.id, priceId, search, PaymentError.INTERNAL_ERROR));
        return;
      }
      replace(genPaymentV2Path(search, product.id, priceId, 'success', undefined));
    } catch (e) {
      Logger.error('[usePaypalFlowNew]:[processPayment]: unknown error', e);
      replace(genPaymentV2ErrorPath(product.id, priceId, search, PaymentError.INTERNAL_ERROR));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    loading,
    step,
    tokenResponse,
    onPaypalApprove,
    processPayment,
  };
};
